import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface ReviewPhotoLightboxProps {
    photos: string[];
    startIndex?: number;
    onClose: () => void;
    // Optional caption (e.g. reviewer name) shown under the image.
    caption?: string;
}

// Fullscreen review-photo viewer. Rendered via portal to document.body so
// position: fixed works with the shop's transformed scroll container (see
// docs/LIGHTBOX_MODAL_PATTERN.md).
const ReviewPhotoLightbox: React.FC<ReviewPhotoLightboxProps> = ({ photos, startIndex = 0, onClose, caption }) => {
    const [index, setIndex] = useState(startIndex);
    const hasMultiple = photos.length > 1;

    const go = useCallback(
        (delta: number) => {
            setIndex((prev) => (prev + delta + photos.length) % photos.length);
        },
        [photos.length],
    );

    // Lock body scroll while open.
    useEffect(() => {
        const originalStyle = window.getComputedStyle(document.body).overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, []);

    // Keyboard: Escape closes, arrows navigate.
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (hasMultiple && e.key === 'ArrowLeft') go(-1);
            if (hasMultiple && e.key === 'ArrowRight') go(1);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [hasMultiple, go, onClose]);

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const content = (
        <div
            className="fixed inset-0 bg-black/80 z-[1000] flex items-center justify-center p-4"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="Review photo viewer"
        >
            <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-[1001]"
            >
                <X size={28} />
            </button>

            {hasMultiple && (
                <button
                    onClick={() => go(-1)}
                    aria-label="Previous photo"
                    className="absolute left-3 md:left-6 text-white/80 hover:text-white p-2 z-[1001]"
                >
                    <ChevronLeft size={36} />
                </button>
            )}

            <div className="flex flex-col items-center max-w-[92vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <img
                    src={photos[index]}
                    alt={caption ? `Review photo from ${caption}` : 'Review photo'}
                    className="max-w-[92vw] max-h-[80vh] object-contain rounded-lg"
                />
                <div className="mt-3 text-center text-white/80 text-[13px] font-body">
                    {caption && <span>{caption}</span>}
                    {hasMultiple && (
                        <span className={caption ? 'ml-2' : ''}>
                            {index + 1} / {photos.length}
                        </span>
                    )}
                </div>
            </div>

            {hasMultiple && (
                <button
                    onClick={() => go(1)}
                    aria-label="Next photo"
                    className="absolute right-3 md:right-6 text-white/80 hover:text-white p-2 z-[1001]"
                >
                    <ChevronRight size={36} />
                </button>
            )}
        </div>
    );

    return createPortal(content, document.body);
};

export default ReviewPhotoLightbox;
